import moment from 'moment';

import Vue from 'vue';
import Vuex from 'vuex';
import bedBoardUtils from './bedBoardUtils';
import eventbus from './eventbus';

import appSettings from './appSettings';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    actionableStayTypeCodes: [],
    alertCodes: [],
    clientImages: [],
    dischargeStatusCodes: [],
    hierarchy: [],
    isLogoutDisabled: false,
    isNavigationVisible: false,
    isOccupancyReportViewable: false,
    myUnitIDs: [],
    myUnits: [],
    occupancyReportPayload: null,
    refreshEveryXSeconds: (60 * 5),
    roomSize: 3,
    roomStatusCodes: [],
    roomTypeCodes: [],
    scopeID: null,
    selectedUnit: null,
    signalRConnection: null,
    transitionGroupNameForStayTiles: 'tg-stay-tiles',
    userInfo: null
  },
  mutations: {
    addClientImage: (state, getClientImageOutputPayload) => {
      state.clientImages.push(getClientImageOutputPayload);
    },
    assignStay: (state, params) => {
      if (params && params.stay) {
        const room = bedBoardUtils.getRoomFromHierarchy(state.hierarchy, params.unitID.id, params.roomID.id);
        if (room) {
          if (room.stays) {
            room.stays.push(params.stay);
          } else {
            room.stays = [params.stay];
          }
        }
      }
    },
    clearConfiguration: (state) => {
      state.actionableStayTypeCodes = [];
      state.alertCodes = [];
      state.clientImages = [];
      state.dischargeStatusCodes = [];
      state.hierarchy = [];
      state.isLogoutDisabled = false;
      state.isNavigationVisible = false;
      state.isOccupancyReportViewable = false;
      state.myUnitIDs = [];
      state.myUnits = [];
      state.occupancyReportPayload = null;
      state.refreshEveryXSeconds = (60 * 5);
      state.roomSize = 3;
      state.roomStatusCodes = [];
      state.roomTypeCodes = [];
      state.scopeID = null;
      state.selectedUnit = null;
      state.userID = null;
    },
    deleteClientImage: (state, clientID) => {
      if (clientID && clientID.id) {
        const existingPayload = state.clientImages.find(element => element.selectedClientImage.clientID.id === clientID.id);
        if (existingPayload) {
          state.clientImages.splice(state.clientImages.indexOf(existingPayload), 1);
          eventbus.$emit('clientImageUpdated', clientID.id);
        }
      }
    },
    decreaseRoomSize: (state) => {
      switch (state.roomSize) {
        case 1:
          break;
        case 2:
        case 3:
        case 4:
          state.roomSize -= 1;
          break;
        case 6:
          state.roomSize = 4;
          break;
        case 12:
          state.roomSize = 6;
          break;
        default:
          state.roomSize = 3;
      }
    },
    disableTransitionForStayTiles: (state) => {
      state.transitionGroupNameForStayTiles = 'off';
    },
    enableTransitionForStayTiles: (state) => {
      state.transitionGroupNameForStayTiles = 'tg-stay-tiles';
    },
    increaseRoomSize: (state) => {
      switch (state.roomSize) {
        case 1:
        case 2:
        case 3:
          state.roomSize += 1;
          break;
        case 4:
          state.roomSize = 6;
          break;
        case 6:
          state.roomSize = 12;
          break;
        case 12:
          break;
        default:
          state.roomSize = 3;
      }
    },
    moveStay: (state, params) => {
      let fromRoom = null;
      let fromRoomStay = null;

      if (params && params.fromValues) {
        fromRoom = bedBoardUtils.getRoomFromHierarchy(state.hierarchy, params.fromValues.unitID.id, params.fromValues.roomID.id);
        fromRoomStay = bedBoardUtils.getStayFromRoom(fromRoom, params.fromValues.stayID.id);
      }

      let toRoom = null;
      if (params && params.toValues) {
        toRoom = bedBoardUtils.getRoomFromHierarchy(state.hierarchy, params.toValues.unitID.id, params.toValues.roomID.id);
      }

      if (fromRoomStay && toRoom) {
        fromRoomStay.bedID = params.toValues.bedID;
        fromRoom.stays.splice(fromRoom.stays.indexOf(fromRoomStay), 1);
        // NOTE: Each room must have stays as an array, even an empty array.  This is enforced in the webapi method for config.
        toRoom.stays.push(fromRoomStay);
      }
    },
    removeStay: (state, params) => {
      let room = null;
      let stay = null;
      if (params) {
        room = bedBoardUtils.getRoomFromHierarchy(state.hierarchy, params.unitID.id, params.roomID.id);
        stay = bedBoardUtils.getStayFromRoom(room, params.stayID.id);

        if (room && stay) {
          room.stays.splice(room.stays.indexOf(stay), 1);
        }
      }
    },
    setConfiguration: (state, params) => {
      const clonsedParams = JSON.parse(JSON.stringify(params));
      state.actionableStayTypeCodes = clonsedParams.config.actionableStayTypeCodes;
      state.alertCodes = clonsedParams.config.alertCodes;
      state.dischargeStatusCodes = clonsedParams.config.dischargeStatusCodes;
      state.hierarchy = clonsedParams.config.hierarchy;
      state.isOccupancyReportViewable = clonsedParams.config.isOccupancyReportViewable;
      state.myUnitIDs = clonsedParams.config.userPreferences.selectedUnitIDs;
      state.refreshEveryXSeconds = clonsedParams.config.refreshEveryXSeconds;
      state.roomSize = 12 / clonsedParams.config.userPreferences.roomColumnCount;
      state.roomStatusCodes = clonsedParams.config.roomStatusCodes;
      state.roomTypeCodes = clonsedParams.config.roomTypeCodes;
      state.scopeID = clonsedParams.scopeID;

      if (clonsedParams.config.hierarchy && clonsedParams.config.userPreferences.selectedUnitIDs) {
        const myUnits = bedBoardUtils.getUnitsFromHierarchy(clonsedParams.config.hierarchy, clonsedParams.config.userPreferences.selectedUnitIDs.map(cf => cf.id));
        bedBoardUtils.initializeUnits(myUnits);
        state.myUnits = myUnits;
        bedBoardUtils.sortUnits(state.myUnits);
      }
    },
    setIsLogoutDisabled: (state, isLogoutDisabled) => {
      state.isLogoutDisabled = isLogoutDisabled;
    },

    setMyUnits: (state, myUnits) => {
      if (myUnits && myUnits.length > 1) {
        state.myUnits = bedBoardUtils.sortUnits(myUnits);
      } else {
        state.myUnits = myUnits;
      }
    },
    setNavigationVisibility: (state, isVisible) => {
      state.isNavigationVisible = isVisible;
    },

    setOccupancyReportPayload: (state, payload) => {
      state.occupancyReportPayload = payload;
    },

    setSelectedUnit: (state, selectedUnit) => {
      state.selectedUnit = selectedUnit;
    },
    setSignalRConnection: (state, connection) => {
      state.signalRConnection = connection;
    },
    setUserInfo: (state, userInfo) => {
      state.userInfo = userInfo;
    },
    setUserPreferenceSelectedUnitIDs: (state, selectedUnitIDs) => {
      state.myUnitIDs = selectedUnitIDs;
      if (state.hierarchy && state.myUnitIDs) {
        state.myUnits = bedBoardUtils.getUnitsFromHierarchy(state.hierarchy, selectedUnitIDs.map(cf => cf.id));
        bedBoardUtils.sortUnits(state.myUnits);
        state.selectedUnit = null;
      }
    },
    swapStays: (state, params) => {
      let fromRoom = null;
      let fromRoomStay = null;
      if (params && params.fromValues) {
        fromRoom = bedBoardUtils.getRoomFromHierarchy(state.hierarchy, params.fromValues.unitID.id, params.fromValues.roomID.id);
        fromRoomStay = bedBoardUtils.getStayFromRoom(fromRoom, params.fromValues.stayID.id);
      }

      let withRoom = null;
      let withRoomStay = null;
      if (params && params.withValues) {
        withRoom = bedBoardUtils.getRoomFromHierarchy(state.hierarchy, params.withValues.unitID.id, params.withValues.roomID.id);
        withRoomStay = bedBoardUtils.getStayFromRoom(withRoom, params.withValues.stayID.id);
      }

      if (fromRoomStay && withRoomStay) {
        // remove
        fromRoom.stays.splice(fromRoom.stays.indexOf(fromRoomStay), 1);
        withRoom.stays.splice(withRoom.stays.indexOf(withRoomStay), 1);

        // switch bed assignments
        const fromBedID = fromRoomStay.bedID;
        const withBedID = withRoomStay.bedID;
        fromRoomStay.bedID = withBedID;
        withRoomStay.bedID = fromBedID;

        // add to each other
        if (fromRoom.stays) {
          fromRoom.stays.push(withRoomStay);
        } else {
          fromRoom.Stays = [withRoomStay];
        }

        if (withRoom.stays) {
          withRoom.stays.push(fromRoomStay);
        } else {
          withRoom.Stays = [fromRoomStay];
        }
      }
    },
    updateClientImage: (state, clientImage) => {
      if (clientImage && clientImage.clientID && clientImage.clientID.id) {
        const existingPayload = state.clientImages.find(element => element.selectedClientImage.clientID.id === clientImage.clientID.id);
        if (existingPayload) {
          existingPayload.selectedClientImage.encodedImage = clientImage.encodedImage;
        } else {
          const newPayload = {
            selectedClientImage: clientImage
          };
          state.clientImages.push(newPayload);
        }

        eventbus.$emit('clientImageUpdated', clientImage.clientID.id);
      }
    },
    updateRoomValues: (state, params) => {
      // find in hiearchy
      const room = bedBoardUtils.getRoomFromHierarchyWithRoomID(state.hierarchy, params.roomID);
      if (room) {
        room.currentNote = params.values.note;
        room.roomStatusCode = params.values.roomStatusCode;
        room.roomTypeCode = params.values.roomTypeCode;
      }
    },
    updateStay: (state, stay) => {
      const room = bedBoardUtils.getRoomFromHierarchyWithStayID(state.hierarchy, stay.bedBoardStayID);
      if (room) {
        const currentStay = room.stays.find(element => element.bedBoardStayID.id === stay.bedBoardStayID.id);
        room.stays.splice(room.stays.indexOf(currentStay), 1);
        room.stays.push(stay);
      }
    },
    updateStayValues: (state, params) => {
      // find in hiearchy
      const room = bedBoardUtils.getRoomFromHierarchyWithStayID(state.hierarchy, params.stayID);
      if (room && room.stays) {
        const stay = bedBoardUtils.getStayFromRoom(room, params.stayID.id);
        if (stay) {
          stay.bedBoardAlerts = params.values.alerts;
          stay.bedID = params.values.bedID;
          if (params.values.clientSummary) {
            stay.clientSummary = params.values.clientSummary;
          }
          stay.dischargeDescription = params.values.dischargeDescription;
          stay.dischargeStatusCode = params.values.dischargeStatusCode;
          stay.expectedDischargeDate = params.values.expectedDischargeDate;
          stay.reservationDescription = params.values.reservationDescription;
          stay.reservationStatusCode = params.values.reservationStatusCode;
          stay.staffNotes = params.values.staffNotes;
          stay.stayTypeCode = params.values.stayTypeCode;
        }
      }
    }
  },
  actions: {
    generateOccupancyReportPayload(context) {
      return new Promise(async (resolve, reject) => {
        if (context.getters.userInfo && context.getters.userInfo.providerID && context.getters.scopeID) {
          bedBoardUtils.executeGenerateBedBoardOccupancyReport({
            id: context.getters.userInfo.providerID,
            scopeID: context.getters.scopeID
          })
            .then((data) => {
              if (data && data.value) {
                context.commit('setOccupancyReportPayload', data.value);
                resolve();
              } else {
                reject(new Error('Generating Occupancy Report did not return any data.'));
              }
            })
            .catch((err) => {
              reject(err);
            });
        } else {
          reject(new Error('no provderID'));
        }
      });
    },

    getClientImage(context, clientID) {
      return new Promise(async (resolve, reject) => {
        if (clientID && context.getters.scopeID) {
          const getClientImageOutputPayload = context.getters.clientImages.find(element => element.selectedClientImage.clientID.id === clientID);
          if (!getClientImageOutputPayload) {
            const params = {
              scopeID: context.getters.scopeID,
              clientID
            };
            bedBoardUtils.executeGetClientImage(params)
              .then((data) => {
                if (data && data.value && data.value.selectedClientImage) {
                  context.commit('addClientImage', data.value);
                  resolve(data.value);
                }
              })
              .catch((err) => {
                this.$log.error(`[${moment().format(appSettings.logDateFormat)}] getClientImage: '${err.message}' `);
                reject(err);
              });
          } else {
            resolve(getClientImageOutputPayload);
          }
        } else {
          reject(new Error('no clientID or scope'));
        }
      });
    },
    loadConfiguration(context, params) {
      return new Promise(async (resolve, reject) => {
        if (params.providerID && params.scope) {
          bedBoardUtils.executeConfig(params)
            .then((data) => {
              if (data && data.value) {
                context.commit('setConfiguration', {
                  config: data.value,
                  scopeID: params.scope
                });
                resolve();
              } else {
                reject(new Error('LoadConfiguration did not return all required data values.'));
              }
            })
            .catch((err) => {
              reject(err);
            });
        } else {
          reject(new Error('no providerID or scope'));
        }
      });
    }
  },
  getters: {
    actionableStayTypeCodes: state => state.actionableStayTypeCodes,
    alertCodes: state => state.alertCodes,
    clientImages: state => state.clientImages,
    dischargeStatusCodes: state => state.dischargeStatusCodes,
    getAvailableBeds: (state, getters) => (roomID, includeStayID = null) => {
      let returnValue = null;

      const room = bedBoardUtils.getRoomFromHierarchyWithRoomID(getters.hierarchy, roomID);
      if (room && room.beds && room.beds.length > 0) {
        returnValue = room.beds.filter((bed) => {
          let isBedAvailable = true;
          if (room.stays) {
            const actionableStayWithAssignedBed = room.stays.find(stay => (getters.isStayActionable(stay) && stay.bedID && bed.bedBoardBedID && stay.bedID.id === bed.bedBoardBedID.id));

            if (actionableStayWithAssignedBed && !(includeStayID && includeStayID.id && actionableStayWithAssignedBed.bedBoardStayID.id === includeStayID.id)) {
              isBedAvailable = false;
            }
          }
          return isBedAvailable;
        });
      }

      return returnValue;
    },
    getDischargeStatusCode: state => (codedEntry) => {
      let returnValue = null;
      if (state.dischargeStatusCodes) {
        returnValue = state.dischargeStatusCodes.find(element => element.code === codedEntry.code);
      }
      return returnValue;
    },
    getLocationNodes: state => () => {
      let returnValue = [];
      if (state.hierarchy) {
        returnValue = bedBoardUtils.getNodesFromHierarchy([state.hierarchy], state.myUnitIDs);
      }
      return returnValue;
    },
    getNumberOfActionableStays: (state, getters) => (room) => {
      const returnValue = room.stays.reduce((stayCount, stay) => {
        let val = stayCount;
        if (getters.isStayActionable(stay)) {
          val = stayCount + 1;
        }
        return val;
      }, 0);
      return returnValue;
    },

    getRoomFilledCount: (state, getters) => (room) => {
      let returnValue = 0;
      if (room && room.stays) {
        returnValue = room.stays.reduce((stayCount, stay) => {
          let val = stayCount;
          if (getters.isStayActionable(stay)) {
            val = stayCount + 1;
          }
          return val;
        }, 0);
      }
      return returnValue;
    },

    getRoomBadgeText: (state, getters) => (room) => {
      let returnValue = null;

      if (room && room.stays) {
        const filledCount = room.stays.reduce((stayCount, stay) => {
          let val = stayCount;
          if (getters.isStayActionable(stay)) {
            val = stayCount + 1;
          }
          return val;
        }, 0);
        returnValue = `${filledCount}/${room.maximumOccupancyCount}`;
      }

      return returnValue;
    },
    getRoomStatusCode: state => (codedEntry) => {
      let returnValue = null;
      if (state.roomStatusCodes) {
        returnValue = state.roomStatusCodes.find(element => element.code === codedEntry.code);
      }
      return returnValue;
    },
    getRoomTypeCode: state => (codedEntry) => {
      let returnValue = null;
      if (state.roomTypeCodes) {
        returnValue = state.roomTypeCodes.find(element => element.code === codedEntry.code);
      }
      return returnValue;
    },
    getStandardAssignedAlertCodes: state => (assignedCodedEntries) => {
      let returnValue = null;
      if (state.alertCodes && assignedCodedEntries) {
        returnValue = state.alertCodes.filter(visualizedCodeEntry => assignedCodedEntries.find(codedEntry => visualizedCodeEntry.code === codedEntry.code));
      }
      return returnValue;
    },
    getUnitAlertCodes: state => (unitID) => {
      const returnValue = [];
      const unit = state.myUnits.find(u => u.bedBoardUnitID.id === unitID);

      if (unit && unit.rooms && unit.rooms.length > 0) {
        unit.rooms.forEach((room) => {
          if (room.stays && room.stays.length > 0) {
            room.stays.forEach((stay) => {
              if (stay.bedBoardAlerts && stay.bedBoardAlerts.length > 0) {
                stay.bedBoardAlerts.forEach((alert) => {
                  returnValue.push({
                    alertName: alert.displayName,
                    clientFirstName: stay.clientSummary.clientName.first,
                    clientLastName: stay.clientSummary.clientName.last,
                    roomName: room.name
                  });
                });
              }
            });
          }
        });
      }
      return returnValue;
    },
    getUnitClientSummaries: state => (unitID) => {
      const returnValue = [];
      const unit = state.myUnits.find(u => u.bedBoardUnitID.id === unitID);

      if (unit && unit.rooms && unit.rooms.length > 0) {
        unit.rooms.forEach((room) => {
          if (room.stays && room.stays.length > 0) {
            room.stays.forEach((stay) => {
              if (stay.clientSummary) {
                returnValue.push({
                  clientSummary: stay.clientSummary,
                  roomName: room.name
                });
              }
            });
          }
        });
      }
      return returnValue;
    },
    hierarchy: state => state.hierarchy,
    isLogoutDisabled: state => state.isLogoutDisabled,
    isNavigationVisible: state => state.isNavigationVisible,
    isOccupancyReportViewable: state => state.isOccupancyReportViewable,
    isStayActionable: state => (stay) => {
      let returnValue = false;

      if (stay && stay.stayTypeCode && stay.stayTypeCode.code) {
        returnValue = state.actionableStayTypeCodes.some(element => element.code === stay.stayTypeCode.code);
      }

      return returnValue;
    },
    myUnitIDs: state => state.myUnitIDs,
    myUnits: state => state.myUnits,
    occupancyReportPayload: state => state.occupancyReportPayload,
    refreshEveryXSeconds: state => state.refreshEveryXSeconds,
    roomSize: state => state.roomSize,
    roomStatusCodes: state => state.roomStatusCodes,
    roomTypeCodes: state => state.roomTypeCodes,
    scopeID: state => state.scopeID,
    selectedUnit: state => state.selectedUnit,
    signalRConnection: state => state.signalRConnection,
    userInfo: state => state.userInfo,
    userPreferences: state => ({
      providerID: {
        id: state.userInfo ? state.userInfo.providerID : null,
        scopeID: state.scopeID
      },
      roomColumnCount: 12 / state.roomSize,
      selectedUnitIDs: state.myUnitIDs
    })
  }
});
